import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { OAuthService } from './oauth.service';
import { Broker } from './broker.services';
import { BrokerAdapter, BrokerOrder, BrokerPosition } from './broker-adapter.interface';

/**
 * BrokerAdapter implementation for Interactive Brokers (IBKR).
 * Delegates all HTTP calls to OAuthService, which handles OAuth 1.0a signing.
 */
@Injectable({
  providedIn: 'root'
})
export class IbrkBrokerAdapterService implements BrokerAdapter {

  private connectedSubject = new BehaviorSubject<boolean>(false);

  constructor(private oauthService: OAuthService) {}

  isConnected(): Observable<boolean> {
    return this.connectedSubject.asObservable();
  }

  // ---------------------------------------------------------------------------
  // Account summary
  // ---------------------------------------------------------------------------

  /**
   * IBKR endpoint: GET /portfolio/{accountId}/summary
   * Maps the verbose IBKR summary object to the app's Broker interface.
   */
  getAccountSummary(accountId: string): Observable<Broker> {
    return this.oauthService.apiGetIbrk(`/portfolio/${accountId}/summary`).pipe(
      map((data: any) => this.mapAccountSummary(data)),
      catchError(err => {
        console.error('[IbrkBrokerAdapter] getAccountSummary error', err);
        return of({ PoderAdquisitivo: 0, ValorLiquidativo: 0, MargenTotal: 0, FondosDisponible: 0, Efectivo: 0 });
      })
    );
  }

  private mapAccountSummary(data: any): Broker {
    const get = (key: string) => data?.[key]?.amount ?? 0;
    return {
      PoderAdquisitivo: get('buyingpower'),
      ValorLiquidativo: get('netliquidation'),
      MargenTotal:      get('grosspositionvalue'),
      FondosDisponible: get('availablefunds'),
      Efectivo:         get('totalcashvalue')
    };
  }

  // ---------------------------------------------------------------------------
  // Positions
  // ---------------------------------------------------------------------------

  /**
   * IBKR endpoint: GET /portfolio/{accountId}/positions/0
   */
  getPositions(accountId: string): Observable<BrokerPosition[]> {
    return this.oauthService.apiGetIbrk(`/portfolio/${accountId}/positions/0`).pipe(
      map((data: any[]) => (Array.isArray(data) ? data : []).map(p => this.mapPosition(p))),
      catchError(err => {
        console.error('[IbrkBrokerAdapter] getPositions error', err);
        return of([]);
      })
    );
  }

  private mapPosition(p: any): BrokerPosition {
    return {
      symbol:        p.ticker ?? p.contractDesc ?? '',
      conid:         p.conid,
      quantity:      p.position ?? 0,
      avgCost:       p.avgCost ?? 0,
      marketPrice:   p.mktPrice ?? 0,
      marketValue:   p.mktValue ?? 0,
      unrealizedPnl: p.unrealizedPnl ?? 0,
      realizedPnl:   p.realizedPnl ?? 0,
      raw: p
    };
  }

  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------

  /**
   * IBKR endpoint: GET /iserver/account/orders
   */
  getOrders(accountId: string): Observable<BrokerOrder[]> {
    return this.oauthService.apiGetIbrk('/iserver/account/orders').pipe(
      map((data: any) => {
        const orders: any[] = data?.orders ?? (Array.isArray(data) ? data : []);
        return orders.map(o => this.mapOrder(o));
      }),
      catchError(err => {
        console.error('[IbrkBrokerAdapter] getOrders error', err);
        return of([]);
      })
    );
  }

  private mapOrder(o: any): BrokerOrder {
    return {
      orderId:       String(o.orderId ?? o.id ?? ''),
      symbol:        o.ticker ?? o.symbol ?? '',
      side:          (o.side ?? '').toUpperCase() === 'S' ? 'SELL' : 'BUY',
      orderType:     this.mapOrderType(o.orderType),
      quantity:      o.totalSize ?? o.quantity ?? 0,
      filledQuantity: o.filledQuantity ?? 0,
      limitPrice:    o.price,
      stopPrice:     o.auxPrice,
      status:        o.status ?? '',
      raw: o
    };
  }

  private mapOrderType(t: string): 'LIMIT' | 'MARKET' | 'STOP' {
    switch ((t ?? '').toUpperCase()) {
      case 'LMT': return 'LIMIT';
      case 'STP': return 'STOP';
      default:    return 'MARKET';
    }
  }

  // ---------------------------------------------------------------------------
  // Place / Cancel order
  // ---------------------------------------------------------------------------

  /**
   * IBKR endpoint: POST /iserver/account/{accountId}/orders
   */
  placeOrder(accountId: string, order: any): Observable<any> {
    return this.oauthService.apiPostIbrk(`/iserver/account/${accountId}/orders`, order).pipe(
      catchError(err => {
        console.error('[IbrkBrokerAdapter] placeOrder error', err);
        return of({ error: err });
      })
    );
  }

  /**
   * IBKR endpoint: DELETE /iserver/account/{accountId}/order/{orderId}
   */
  cancelOrder(accountId: string, orderId: string): Observable<any> {
    return this.oauthService.apiDeleteIbrk(`/iserver/account/${accountId}/order/${orderId}`).pipe(
      catchError(err => {
        console.error('[IbrkBrokerAdapter] cancelOrder error', err);
        return of({ error: err });
      })
    );
  }
}
